import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useFormik } from 'formik';
import * as yup from 'yup';

import SplitTemplate from '../../components/SplitTemplate';
import UnderlinedInput from '../../components/Inputs/UnderlinedInput';
import ConfirmButton from '../../components/Buttons/ConfirmButton';
import useCheckMobileScreen from '../../hooks/useCheckMobileScreen';
import { UiContext } from '../../context/UiContext';
import UserService from '../../services/UserService';
import OnboardingService from '../../services/OnboardingService';
import classes from './styles.module.scss';

  // Helper function to split full name into first/last
  const splitName = (fullName) => {
    if (!fullName) return { firstName: '', lastName: '' };
    const parts = fullName.trim().split(' ');
    return {
      firstName: parts[0] || '',
      lastName: parts.slice(1).join(' ') || '' // Handle multi-part last names
    };
  };

  // Get initial values from user data
  const getInitialValues = (userData) => {
    const { firstName, lastName } = splitName(userData?.name);
    return {
      firstName: firstName || '',
      lastName: lastName || '',
      companyName: userData?.companyName || userData?.Organization?.name || '',
      title: userData?.title || '',
    };
  };

  // Check if onboarding is already complete
  const isOnboardingComplete = (userData) => {
    const name = userData?.name?.trim();
    const companyName = userData?.companyName?.trim() || userData?.Organization?.name?.trim();
    const title = userData?.title?.trim();

    return name && name.length > 0 &&
           companyName && companyName.length > 0 &&
           title && title.length > 0;
  };

  const validationSchema = yup.object({
    firstName: yup
      .string()
      .trim()
      .required('This field is required')
      .matches(/^\S+$/, 'This field cannot contain white space'),
    lastName: yup
      .string()
      .trim()
      .matches(/^\S+$/, 'This field cannot contain white space'), // Optional for single names
    companyName: yup.string().trim().required('This field is required'),
    title: yup.string().trim().required('This field is required'),
  });

export default function OnboardingPage() {
  const navigate = useNavigate();
  const isMobile = useCheckMobileScreen();
  const { notifyError, redirectAfterLogin, setRedirectAfterLogin } =
    useContext(UiContext);
  const formPopulatedRef = useRef(false);

  // Fetch user data for pre-population
  const { data: userData, isLoading: isLoadingUser } = useQuery(
    'me',
    UserService.getMe,
    { enabled: true }
  );

  useEffect(() => {
    const shouldRedirectToDefaultPage = ['/my-videos', '/my-library'].includes(
      redirectAfterLogin
    );

    if (!redirectAfterLogin || shouldRedirectToDefaultPage) {
      setRedirectAfterLogin(isMobile ? '/my-videos' : '/my-library');
    }
  }, [isMobile, redirectAfterLogin, setRedirectAfterLogin]);

  // Auto-complete onboarding if already complete
  useEffect(() => {
    if (!isLoadingUser && userData && isOnboardingComplete(userData)) {
      sessionStorage.setItem('onboardingCompleted', 'true');
      navigate(redirectAfterLogin || (isMobile ? '/my-videos' : '/my-library'));
    }
  }, [userData, isLoadingUser, navigate, redirectAfterLogin, isMobile]);

  const handleSubmit = async (values) => {
    try {
      await OnboardingService.complete(values);
      sessionStorage.setItem('onboardingCompleted', 'true');
      navigate(redirectAfterLogin || (isMobile ? '/my-videos' : '/my-library'));
      if (redirectAfterLogin) {
        setRedirectAfterLogin('');
      }
    } catch (error) {
      notifyError(error?.response?.data?.message || 'Failed to save');
    }
  };

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      companyName: '',
      title: '',
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  // Update form values when userData loads (only once)
  useEffect(() => {
    if (userData && !isLoadingUser && !formPopulatedRef.current) {
      const initialValues = getInitialValues(userData);
      formik.setValues(initialValues);
      formPopulatedRef.current = true;
    }
  }, [userData, isLoadingUser, formik]);

  const hasError = useMemo(
    () => Object.values(formik.errors).some((error) => error),
    [formik.errors]
  );
  const hasEmptyValue = useMemo(
    () => Object.values(formik.values).some((value) => !value.length),
    [formik.values]
  );

  const isSubmitDisabled = hasError || hasEmptyValue;

  const handleSkip = () => {
    sessionStorage.setItem('onboardingCompleted', 'true');
    navigate(redirectAfterLogin || (isMobile ? '/my-videos' : '/my-library'));
    if (redirectAfterLogin) {
      setRedirectAfterLogin('');
    }
  };

  const handleSkipLink = (e) => {
    e.preventDefault();
    sessionStorage.setItem('onboardingCompleted', 'true');
    navigate(redirectAfterLogin || (isMobile ? '/my-videos' : '/my-library'));
    if (redirectAfterLogin) {
      setRedirectAfterLogin('');
    }
  };

  return (
    <div className={classes.OnboardingPage}>
      <SplitTemplate>
        <div className={classes.content}>
          <div className={classes.header}>
            <span className={classes.backToApp}>
              <button type="button" onClick={handleSkipLink} className={classes.skipLink}>Skip for now</button>
            </span>
            <h1>Welcome</h1>
            <p className={classes.subtitle}>
              Tell us a bit more to personalize your workspace. You can change this later.
            </p>
          </div>

          <form onSubmit={formik.handleSubmit}>
            <div className={classes.inputs}>
              <div className={classes.row}>
                <UnderlinedInput
                  label={
                    formik.errors.firstName && formik.touched.firstName
                      ? formik.errors.firstName
                      : 'First name'
                  }
                  value={formik.values.firstName}
                  errors={formik.errors.firstName}
                  isTouched={formik.touched.firstName}
                  type="text"
                  name="firstName"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />

                <UnderlinedInput
                  label={
                    formik.errors.lastName && formik.touched.lastName
                      ? formik.errors.lastName
                      : 'Last name'
                  }
                  value={formik.values.lastName}
                  errors={formik.errors.lastName}
                  isTouched={formik.touched.lastName}
                  type="text"
                  name="lastName"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>

              <UnderlinedInput
                label={
                  formik.errors.companyName && formik.touched.companyName
                    ? formik.errors.companyName
                    : 'Company name'
                }
                value={formik.values.companyName}
                errors={formik.errors.companyName}
                isTouched={formik.touched.companyName}
                type="text"
                name="companyName"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />

              <UnderlinedInput
                label={
                  formik.errors.title && formik.touched.title
                    ? formik.errors.title
                    : 'Title'
                }
                value={formik.values.title}
                errors={formik.errors.title}
                isTouched={formik.touched.title}
                type="text"
                name="title"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </div>

            <div className={classes.actions}>
              <button type="button" className={classes.secondaryButton} onClick={handleSkip}>
                Skip for now
              </button>
              <ConfirmButton isDisabled={isSubmitDisabled} />
            </div>
          </form>
        </div>
      </SplitTemplate>
    </div>
  );
}

